'use client';

import Typography from '../../../atoms/Typography/Typography';
import Iconography from '../../../atoms/Iconography/Iconography';
import Checkbox from '../../../atoms/Checkbox/Checkbox';
import Box from '../../../atoms/Box/Box';
import type { TableColumn } from '../types';
import { getColumnStyle, getAlignClass } from '../utils';
import styles from './Header.module.scss';

type SortDirection = 'asc' | 'desc' | null;

type HeaderProps<T> = {
  columns: TableColumn<T>[];
  theme: 'light' | 'dark';
  selectable: boolean;
  isAllSelected: boolean;
  sortColumn: string | null;
  sortDirection: SortDirection;
  onSort: (columnKey: string) => void;
  onSelectAll: (checked: boolean) => void;
};

/**
 * Table header component that renders column titles with sort indicators.
 *
 * @component
 * @param {HeaderProps<T>} props - Component props
 * @param {TableColumn<T>[]} props.columns - Column configuration array
 * @param {'light' | 'dark'} props.theme - Theme variant
 * @param {boolean} props.selectable - Whether row selection is enabled
 * @param {boolean} props.isAllSelected - Whether all rows are selected
 * @param {string | null} props.sortColumn - Currently sorted column key
 * @param {SortDirection} props.sortDirection - Current sort direction
 * @param {function} props.onSort - Callback when a column header is clicked for sorting
 * @param {function} props.onSelectAll - Callback when select all checkbox is toggled
 * @returns {React.ReactElement} The Header component
 */
const Header = <T extends Record<string, unknown>>({
  columns,
  theme,
  selectable,
  isAllSelected,
  sortColumn,
  sortDirection,
  onSort,
  onSelectAll,
}: HeaderProps<T>) => {
  return (
    <thead className={styles.tableHeader} data-theme={theme}>
      <tr>
        <th className={`${styles.tableHeaderCell} ${styles.rowNumberCell}`} />
        {selectable && (
          <th className={`${styles.tableHeaderCell} ${styles.checkboxCell}`}>
            <Box className={styles.checkboxWrapper}>
              <Checkbox
                isChecked={isAllSelected}
                handleChange={onSelectAll}
                dataTestId="table-select-all"
                gaComponent="table-select-all"
                theme={theme}
                logEvents={false}
                showValidation={false}
              />
            </Box>
          </th>
        )}
        {columns.map((column) => {
          const isActiveSort = sortColumn === column.key && sortDirection !== null;
          const columnStyle = {
            ...getColumnStyle(column),
            textAlign: column.align || 'left',
          } as React.CSSProperties;

          return (
            <th
              key={column.key}
              className={`${styles.tableHeaderCell} ${column.sortable !== false ? styles.sortable : ''} ${isActiveSort ? styles.activeSort : ''}`}
              // eslint-disable-next-line react/forbid-dom-props
              style={columnStyle}
              onClick={() => column.sortable !== false && onSort(column.key)}
            >
              <Box className={`${styles.headerContent} ${getAlignClass(column.align, styles)}`}>
                <Typography variant="body" size="small" color="white" theme={theme}>
                  {column.title}
                </Typography>
                {column.sortable !== false && (
                  <span
                    className={`${styles.sortIcon} ${isActiveSort ? styles.activeSortIcon : ''} ${isActiveSort && sortDirection === 'desc' ? styles.sortIconRotated : ''}`}
                  >
                    <Iconography category="arrows" name="chevron-down" color="black" />
                  </span>
                )}
              </Box>
            </th>
          );
        })}
      </tr>
    </thead>
  );
};

export default Header;
